// Copie locale des projets ouverts, lue quand le réseau manque. Un enregistrement d'élément porte « server » (dernière
// version du serveur), « local » (saisie pas encore acceptée, { deleted: true } pour une suppression) et « removed »
// (supprimé côté serveur). Arbres : blocs binaires du dernier chargement, plus un enregistrement par arbre changé depuis.
// Toute lecture suivie d'une écriture se fait dans une transaction « rw », pour que deux appels simultanés ne s'écrasent pas.

// Version à afficher : la saisie locale, sinon la version serveur ; null si supprimé
export const getDisplayFeature = (record) => {
    if (!record || record.removed) return null;
    if (record.local?.deleted) return null;
    return record.local || record.server || null;
};

const now = () => new Date().toISOString();

export const createProjectSnapshot = (db) => {
    // Projet ouvert dans ProjectMap : seules ses lectures alimentent la copie
    let activeProjectId = null;
    // Chargement complet en cours : à la fin, ce qui n'a pas été revu a été supprimé ailleurs
    let loadId = null;
    // Un enregistrement écrit pendant le chargement en cours lui appartient, sinon completeLoad l'effacerait
    const loadIdFor = (projectId, existing) => (snapshot.isActive(projectId) && loadId) || existing?.loadId || null;
    // Met à jour un projet en le créant s'il n'existe pas encore
    const upsertProject = (pid, changes) => db.transaction('rw', db.projects, async () => {
        const existing = await db.projects.get(pid);
        await db.projects.put({ ...existing, ...changes, id: pid });
    });

    const snapshot = {
        // Déclare le projet ouvert
        setActiveProject: (projectId) => {
            activeProjectId = Number(projectId);
            loadId = null;
        },
        // Plus aucun projet ouvert : la copie n'est plus alimentée
        clearActiveProject: () => {
            activeProjectId = null;
            loadId = null;
        },
        // Indique si ce projet est celui ouvert
        isActive: (projectId) => projectId !== null && projectId !== undefined && Number(projectId) === activeProjectId,
        // Démarre un chargement complet du projet ouvert
        beginLoad: (projectId) => {
            if (snapshot.isActive(projectId)) loadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            return loadId;
        },

        // --- Projet
        // Enregistre le projet (objet projet, droits, collaborateurs…) et sa date d'ouverture
        saveProject: (projectId, data) => upsertProject(Number(projectId), { ...data, savedAt: now(), lastOpenedAt: now() }),
        // Met à jour la date de dernière ouverture (purge des copies anciennes)
        touchProject: (projectId) => upsertProject(Number(projectId), { lastOpenedAt: now() }),
        // Lit l'enregistrement d'un projet
        getProject: (projectId) => db.projects.get(Number(projectId)),
        // Projets copiés, du plus récemment ouvert au plus ancien
        listProjects: () => db.projects.orderBy('lastOpenedAt').reverse().toArray(),
        // Supprime toute la copie d'un projet
        deleteProject: (projectId) => db.transaction('rw', db.projects, db.elements, db.treeChunks, db.lists, async () => {
            await db.projects.delete(Number(projectId));
            await db.elements.where('projectId').equals(Number(projectId)).delete();
            await db.treeChunks.where('projectId').equals(Number(projectId)).delete();
            await db.lists.where('projectId').equals(Number(projectId)).delete();
        }),

        // --- Éléments
        // Enregistre les versions serveur reçues pour le projet ouvert, en gardant les saisies locales
        saveServerElements: (projectId, category, features) => {
            if (!snapshot.isActive(projectId) || !Array.isArray(features) || !features.length) return Promise.resolve();
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const existing = await db.elements.bulkGet(features.map(feature => [pid, feature.id]));
                await db.elements.bulkPut(features.map((feature, index) => ({
                    projectId: pid, id: feature.id, category,
                    server: feature,
                    local: existing[index]?.local ?? null,
                    removed: false,
                    loadId: loadIdFor(projectId, existing[index]),
                    savedAt: now()
                })));
            });
        },
        // Fin du chargement complet d'une catégorie : retire ce qui n'a pas été revu, sauf les saisies locales
        completeLoad: async (projectId, category) => {
            if (!snapshot.isActive(projectId) || !loadId) return;
            const currentLoadId = loadId;
            await db.elements.where('[projectId+category]').equals([Number(projectId), category])
                .filter(record => record.loadId !== currentLoadId && !record.local)
                .delete();
        },
        // Remplace toute une catégorie lue d'un seul appel (repères, stations), sans toucher aux saisies locales
        replaceServerElements: (projectId, category, features) => {
            if (!snapshot.isActive(projectId) || !Array.isArray(features)) return Promise.resolve();
            return db.transaction('rw', db.elements, async () => {
                await db.elements.where('[projectId+category]').equals([Number(projectId), category]).filter(record => !record.local).delete();
                await snapshot.saveServerElements(projectId, category, features);
            });
        },
        // Date du dernier chargement complet réussi (affichée pour « Ouvrir la copie locale du … »)
        markLoaded: (projectId) => upsertProject(Number(projectId), { loadedAt: now() }),
        // Note des suppressions faites côté serveur ; l'enregistrement « removed » masque aussi la ligne d'un bloc binaire
        removeElements: (projectId, category, ids) => {
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const existing = await db.elements.bulkGet(ids.map(id => [pid, id]));
                await db.elements.bulkPut(ids.map((id, index) => ({
                    projectId: pid, id, category: existing[index]?.category ?? category,
                    server: null, local: null, removed: true,
                    loadId: loadIdFor(projectId, existing[index]), savedAt: now()
                })));
            });
        },
        // Enregistre une saisie faite sur l'appareil, affichée jusqu'à son acceptation
        putLocal: (projectId, category, feature) => {
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const existing = await db.elements.get([pid, feature.id]);
                await db.elements.put({
                    projectId: pid, id: feature.id, category, server: existing?.server ?? null, local: feature, removed: false,
                    loadId: loadIdFor(projectId, existing), savedAt: now()
                });
            });
        },
        // Note une suppression faite sur l'appareil ; un élément créé localement et jamais envoyé est simplement retiré
        markLocalDeleted: (projectId, category, ids, { createdLocally = [] } = {}) => {
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const records = await db.elements.bulkGet(ids.map(id => [pid, id]));
                await db.elements.bulkDelete(ids.filter(id => createdLocally.includes(id)).map(id => [pid, id]));
                await db.elements.bulkPut(ids.flatMap((id, index) => createdLocally.includes(id) ? [] : [{
                    projectId: pid, id, category: records[index]?.category ?? category,
                    server: records[index]?.server ?? null, local: { deleted: true }, removed: false,
                    loadId: records[index]?.loadId ?? null, savedAt: now()
                }]));
            });
        },
        // Réponse de l'API : devient la version serveur ; la saisie locale est effacée si plus rien n'attend pour l'élément
        acknowledge: (projectId, category, serverFeature, { clearLocal = true } = {}) => {
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const existing = await db.elements.get([pid, serverFeature.id]);
                await db.elements.put({
                    projectId: pid, id: serverFeature.id, category, server: serverFeature,
                    local: clearLocal ? null : (existing?.local ?? null), removed: false,
                    loadId: loadIdFor(projectId, existing), savedAt: now()
                });
            });
        },
        // Abandonne la saisie locale et revient à la version serveur (retire l'enregistrement s'il n'en a pas)
        revertLocal: (projectId, id) => {
            const pid = Number(projectId);
            return db.transaction('rw', db.elements, async () => {
                const existing = await db.elements.get([pid, id]);
                if (!existing) return;
                if (existing.server) await db.elements.put({ ...existing, local: null });
                else await db.elements.delete([pid, id]);
            });
        },
        // Lit l'enregistrement d'un élément
        getElement: (projectId, id) => db.elements.get([Number(projectId), id]),
        // Lit tous les enregistrements d'éléments d'un projet
        getElements: (projectId) => db.elements.where('projectId').equals(Number(projectId)).toArray(),
        // Lit les enregistrements d'une catégorie
        getElementsByCategory: (projectId, category) => db.elements.where('[projectId+category]').equals([Number(projectId), category]).toArray(),

        // --- Blocs binaires des arbres
        // Enregistre un bloc binaire d'arbres tel que reçu de l'API, avec son rang de rendu
        saveTreeChunk: async (projectId, { order, buffer, formatVersion }) => {
            if (!snapshot.isActive(projectId) || !loadId || !buffer) return;
            await db.treeChunks.put({ projectId: Number(projectId), loadId, order, buffer, formatVersion, savedAt: now() });
        },
        // Fin du chargement des arbres : les nouveaux blocs remplacent les anciens s'ils sont tous là ; renvoie false sinon
        completeTreeLoad: (projectId, { binary, expectedChunks } = {}) => {
            if (!snapshot.isActive(projectId) || !loadId) return Promise.resolve(false);
            const pid = Number(projectId);
            const currentLoadId = loadId;
            return db.transaction('rw', db.elements, db.treeChunks, db.projects, async () => {
                if (binary && expectedChunks !== undefined) {
                    const received = await db.treeChunks.where('[projectId+loadId]').equals([pid, currentLoadId]).count();
                    if (received !== expectedChunks) {
                        await db.treeChunks.where('[projectId+loadId]').equals([pid, currentLoadId]).delete();
                        return false;
                    }
                }
                await snapshot.completeLoad(projectId, 'Arbre');
                await db.treeChunks.where('projectId').equals(pid).filter(chunk => !binary || chunk.loadId !== currentLoadId).delete();
                // loadedAt (copie complète) : posé par ProjectMap une fois toutes les catégories chargées (markLoaded)
                await upsertProject(pid, { treeLoadId: binary ? currentLoadId : null });
                return true;
            });
        },
        // Blocs du dernier chargement complet, dans l'ordre de rendu
        getTreeChunks: async (projectId) => {
            const pid = Number(projectId);
            const treeLoadId = (await db.projects.get(pid))?.treeLoadId;
            if (!treeLoadId) return [];
            return db.treeChunks.where('[projectId+loadId]').equals([pid, treeLoadId]).sortBy('order');
        },

        // --- Listes (occurrences d'actions, évènements, liaisons, champs personnalisés, statistiques…)
        // Enregistre une liste du projet ouvert (occurrences d'actions, évènements, liaisons…)
        saveList: async (projectId, name, data) => {
            if (!snapshot.isActive(projectId) || data === undefined) return;
            await db.lists.put({ projectId: Number(projectId), name, data, savedAt: now() });
        },
        // Lit une liste
        getList: async (projectId, name) => (await db.lists.get([Number(projectId), name]))?.data,
        // Modifie une liste par une saisie locale
        updateList: (projectId, name, updater) => db.transaction('rw', db.lists, async () => {
            const current = (await db.lists.get([Number(projectId), name]))?.data;
            await db.lists.put({ projectId: Number(projectId), name, data: updater(current), savedAt: now() });
        })
    };
    return snapshot;
};
